import { useEffect, useRef, useState } from 'react'
import { useKioskStore } from '@/stores/kiosk-store'

const DEFAULT_INACTIVITY_TIMEOUT = 60_000 // 60 seconds untouched
const DEFAULT_COUNTDOWN_DURATION = 10_000 // 10 seconds countdown on screen before reset

export function useInactivity() {
  const step = useKioskStore((s) => s.step)
  const resetToAttractor = useKioskStore((s) => s.resetToAttractor)
  const remoteConfig = useKioskStore((s) => s.remoteConfig)

  // Remote config stores seconds, code uses milliseconds
  const INACTIVITY_TIMEOUT = remoteConfig?.inactivityTimeout ? remoteConfig.inactivityTimeout * 1000 : DEFAULT_INACTIVITY_TIMEOUT
  const COUNTDOWN_DURATION = remoteConfig?.countdownDuration ? remoteConfig.countdownDuration * 1000 : DEFAULT_COUNTDOWN_DURATION
  const countdownSeconds = Math.round(COUNTDOWN_DURATION / 1000)

  const [showWarning, setShowWarning] = useState(false)
  const [countdown, setCountdown] = useState(countdownSeconds)

  // Use refs to avoid stale closures and to prevent useEffect re-runs from
  // clearing our active countdown when showWarning/countdown state changes
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const showWarningRef = useRef(false)
  const stepRef = useRef(step)
  stepRef.current = step
  showWarningRef.current = showWarning

  // showWarning/step are read via refs above so we can intentionally avoid
  // re-running this effect when those change — doing so was a bug that
  // cleared the countdown as soon as it started.
  useEffect(() => {
    const shouldSkip = (s: typeof step) => s === 'attractor' || s === 'menuTypeSelect' || s === 'paying' || s === 'receipt'

    const clearTimers = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current)
        countdownRef.current = null
      }
    }

    const startInactivityTimer = () => {
      clearTimers()
      if (shouldSkip(stepRef.current)) return

      timerRef.current = setTimeout(() => {
        // Inactivity reached — show warning and start countdown
        setShowWarning(true)
        setCountdown(countdownSeconds)
        let remaining = countdownSeconds
        countdownRef.current = setInterval(() => {
          remaining -= 1
          if (remaining <= 0) {
            clearTimers()
            setShowWarning(false)
            setCountdown(countdownSeconds)
            resetToAttractor()
          } else {
            setCountdown(remaining)
          }
        }, 1000)
      }, INACTIVITY_TIMEOUT)
    }

    const onActivity = () => {
      if (showWarningRef.current) {
        // User tapped during countdown — cancel and restart
        clearTimers()
        setShowWarning(false)
        setCountdown(countdownSeconds)
      }
      startInactivityTimer()
    }

    window.addEventListener('touchstart', onActivity, { passive: true })
    window.addEventListener('mousedown', onActivity)
    window.addEventListener('scroll', onActivity, { passive: true })

    startInactivityTimer()

    return () => {
      clearTimers()
      window.removeEventListener('touchstart', onActivity)
      window.removeEventListener('mousedown', onActivity)
      window.removeEventListener('scroll', onActivity)
    }
  }, [resetToAttractor, countdownSeconds, INACTIVITY_TIMEOUT])

  const dismiss = () => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current)
      countdownRef.current = null
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setShowWarning(false)
    setCountdown(countdownSeconds)
    // onActivity will re-start the timer via the event listener, but user
    // might dismiss without touching — start explicitly just in case
  }

  return { showWarning, countdown, dismiss }
}
